import React, { useEffect, useState } from "react";
import { Loader2, Compass, RefreshCw, AlertTriangle, FileWarning, Scale, Gavel, ClipboardCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const RISK_COLORS = {
  "låg": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "medel": "bg-amber-50 text-amber-800 border-amber-200",
  "hög": "bg-red-50 text-red-800 border-red-200",
};

const LOVPLIKT_LABELS = {
  bygglov: "Bygglovspliktig",
  anmalan: "Anmälningspliktig",
  lovbefriad: "Lovbefriad",
  attefall: "Attefall – anmälan räcker",
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="border border-zinc-200 rounded-sm p-4">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-zinc-500">
      <Icon className="w-4 h-4 text-blue-700" /> {title}
    </div>
    <div className="mt-2 text-sm text-zinc-700 leading-relaxed">{children}</div>
  </div>
);

export const CaseAtgardAnalys = ({ caseId }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/crm/cases/${caseId}/atgardsidentifiering`).then((r) => setResult(r.data)).catch(() => {});
  }, [caseId]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/crm/cases/${caseId}/atgardsidentifiering`);
      setResult(data);
      toast.success("Åtgärdsidentifiering klar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI-analysen misslyckades");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="atgard-analys">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Compass className="w-5 h-5 text-blue-700 stroke-[1.5]" />
          <h3 className="font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>AI: Åtgärdsidentifiering</h3>
        </div>
        <button onClick={run} disabled={loading} data-testid="atgard-run"
          className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white px-4 py-2 rounded-sm text-sm font-medium">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyserar…</> : result ? <><RefreshCw className="w-4 h-4" /> Kör om analys</> : "Kör analys"}
        </button>
      </div>

      {!result && !loading && (
        <p className="mt-3 text-sm text-zinc-500">
          AI:n läser ärendet, handlingarna och checklistan och producerar en strukturerad första bedömning: åtgärdstyp, lovplikt, saknade handlingar, lagrum, rättsfall och riskbedömning.
        </p>
      )}

      {result && (
        <div className="mt-5 space-y-3" data-testid="atgard-result">
          <div className="flex flex-wrap gap-2">
            {result.lovplikt?.bedomning && (
              <span className="px-3 py-1 text-xs font-semibold rounded-sm bg-blue-50 text-blue-800 border border-blue-200" data-testid="atgard-lovplikt-badge">
                {LOVPLIKT_LABELS[result.lovplikt.bedomning] || result.lovplikt.bedomning}
              </span>
            )}
            {result.risk?.niva && (
              <span className={`px-3 py-1 text-xs font-semibold rounded-sm border ${RISK_COLORS[result.risk.niva?.toLowerCase()] || RISK_COLORS["medel"]}`} data-testid="atgard-risk-badge">
                Risk för avslag: {result.risk.niva}
              </span>
            )}
          </div>

          {result.sammanfattning && (
            <div className="bg-blue-50/60 border-l-4 border-blue-700 rounded-r-sm px-4 py-3 text-sm text-zinc-800 leading-relaxed" data-testid="atgard-summary">
              <b>Sammanfattning:</b> {result.sammanfattning}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {result.atgardstyp && (
              <Section icon={ClipboardCheck} title="1. Identifierad åtgärdstyp">
                <b>{result.atgardstyp.typ}</b>
                <p className="mt-1">{result.atgardstyp.beskrivning}</p>
              </Section>
            )}
            {result.lovplikt && (
              <Section icon={Scale} title="2. Lovplikt">
                <p>{result.lovplikt.motivering}</p>
                {result.lovplikt.lagrum?.length > 0 && (
                  <div className="mt-2 text-xs text-zinc-500"><b>Lagrum:</b> {result.lovplikt.lagrum.join(", ")}</div>
                )}
              </Section>
            )}
            {result.saknade_handlingar?.length > 0 && (
              <Section icon={FileWarning} title="3. Saknade handlingar">
                <ul className="space-y-1.5">
                  {result.saknade_handlingar.map((h, i) => (
                    <li key={i}><b>{h.handling}</b> – {h.motivering}</li>
                  ))}
                </ul>
              </Section>
            )}
            {result.lagrum?.length > 0 && (
              <Section icon={Scale} title="4. Relevanta lagrum">
                <ul className="space-y-1.5">
                  {result.lagrum.map((l, i) => (
                    <li key={i}><b>{l.ref}</b> – {l.varfor}</li>
                  ))}
                </ul>
              </Section>
            )}
            {result.rattsfall?.length > 0 && (
              <Section icon={Gavel} title="5. Relevanta rättsfall">
                <ul className="space-y-1.5">
                  {result.rattsfall.map((r, i) => (
                    <li key={i}><b>{r.ref}</b> – {r.varfor}</li>
                  ))}
                </ul>
              </Section>
            )}
            {result.risk && (
              <Section icon={AlertTriangle} title="6. Riskbedömning">
                <p>{result.risk.motivering}</p>
              </Section>
            )}
          </div>

          {result.generated_at && (
            <div className="text-[11px] text-zinc-400">
              Genererad {result.generated_at.slice(0, 16).replace("T", " ")} av {result.generated_by || "AI"} · AI-bedömning – vägledande, kontrollera alltid mot detaljplan och PBL.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CaseAtgardAnalys;
